import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { loadLibrary, loadMuscles, muscles, muscleName } from "../store.js";
import { openModal, spinner } from "./ui.js";
import { MuscleMap } from "./MuscleMap.js";
import { icon } from "../lib/icons.js";

// ── Trainer knowledge ───────────────────────────────────────────────────────
// Muscle ids: 1 Abs · 2 Abductors · 3 Adductors · 4 Biceps · 5 Calves · 6 Chest ·
// 7 Forearms · 8 Glutes · 9 Hamstrings · 10 Lats · 11 Lower Back · 12 Middle Back ·
// 13 Neck · 14 Quadriceps · 15 Shoulders · 16 Traps · 17 Triceps.

// Curated splits — the muscle pairings trainers actually program together (PPL,
// upper/lower, bro-split pairs). Each carries its target muscles + a recommended
// exercise count so the generator can build a balanced session.
const FOCUSES = [
  { id: "full_body", label: "Full Body", emoji: "🧍", sub: "Chest · Back · Legs · Shoulders", muscles: [6, 10, 14, 9, 15, 8, 1], count: 6 },
  { id: "upper", label: "Upper Body", emoji: "💪", sub: "Chest · Back · Shoulders · Arms", muscles: [6, 10, 12, 15, 4, 17], count: 6 },
  { id: "lower", label: "Lower Body", emoji: "🦵", sub: "Quads · Hamstrings · Glutes · Calves", muscles: [14, 9, 8, 5, 11], count: 5 },
  { id: "push", label: "Push", emoji: "⬆️", sub: "Chest · Shoulders · Triceps", muscles: [6, 15, 17], count: 5 },
  { id: "pull", label: "Pull", emoji: "⬇️", sub: "Back · Biceps · Traps", muscles: [10, 12, 4, 16], count: 5 },
  { id: "legs", label: "Legs", emoji: "🦿", sub: "Quads · Hamstrings · Glutes · Calves", muscles: [14, 9, 8, 5], count: 5 },
  { id: "chest_tri", label: "Chest & Triceps", emoji: "🤜", sub: "Classic push pairing", muscles: [6, 17], count: 5 },
  { id: "back_bi", label: "Back & Biceps", emoji: "🎣", sub: "Classic pull pairing", muscles: [10, 12, 4], count: 5 },
  { id: "sho_arms", label: "Shoulders & Arms", emoji: "🏋️", sub: "Delts · Biceps · Triceps", muscles: [15, 4, 17], count: 5 },
  { id: "core", label: "Core", emoji: "🌀", sub: "Abs · Obliques · Lower back", muscles: [1, 11], count: 4 },
  { id: "custom", label: "Custom", emoji: "🎯", sub: "Pick your own muscles", muscles: [], count: 5, custom: true },
  { id: "cardio", label: "Cardio", emoji: "❤️", sub: "Conditioning & heart health", muscles: [], count: 4, category: "cardio" },
  { id: "mobility", label: "Mobility", emoji: "🤸", sub: "Stretching & flexibility", muscles: [], count: 5, category: "stretching" },
];
const FOCUS_BY_ID = Object.fromEntries(FOCUSES.map((f) => [f.id, f]));

// Training goal → set/rep/rest scheme. Compounds get more sets, lower reps and
// longer rest than isolation work — standard periodization for each goal.
const GOALS = [
  { id: "hypertrophy", label: "Build Muscle", emoji: "🏋️", sub: "Moderate reps · short rest" },
  { id: "strength", label: "Get Stronger", emoji: "🔩", sub: "Heavy · low reps · long rest" },
  { id: "endurance", label: "Endurance & Tone", emoji: "🔥", sub: "Light · high reps · short rest" },
];

function scheme(goal, mechanic) {
  const compound = mechanic === "compound";
  if (goal === "strength") return compound ? { sets: 5, min: 3, max: 5, rest: 180 } : { sets: 3, min: 6, max: 8, rest: 150 };
  if (goal === "endurance") return compound ? { sets: 3, min: 12, max: 15, rest: 60 } : { sets: 3, min: 15, max: 20, rest: 45 };
  return compound ? { sets: 4, min: 8, max: 12, rest: 90 } : { sets: 3, min: 10, max: 15, rest: 60 }; // hypertrophy
}
// Cardio / mobility don't fit a sets×reps mould — keep them light and rep-less.
function nonLiftingScheme(category) {
  return category === "cardio" ? { sets: 3, min: null, max: null, rest: 60 } : { sets: 2, min: null, max: null, rest: 20 };
}

// Biggest / most compound-driving muscles first — drives coverage priority and
// the compound-before-isolation ordering of the finished workout.
const PRIORITY = [14, 9, 8, 6, 10, 12, 15, 11, 16, 4, 17, 7, 5, 1, 2, 3, 13];
const prio = (id) => { const i = PRIORITY.indexOf(id); return i === -1 ? 99 : i; };
const isCompound = (ex) => ex.mechanic === "compound";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Component ────────────────────────────────────────────────────────────────
export function openWizard({ onComplete } = {}) {
  const data = { focusId: null, goal: "hypertrophy", muscles: new Set(), equipment: new Set(), count: 5 };
  let step = 0;
  let library = [];
  let generated = [];

  const body = h("div", null, spinner());
  const modal = openModal({ title: "Workout Wizard", content: body, fullscreen: true });
  // Training coverage drives the custom step's gap-picker: muscles you've never trained
  // vs. ones only ever worked indirectly (as a secondary mover). Lowercased names match
  // the body-map's selection vocabulary.
  let gaps = { never: [], indirect: [] };
  Promise.all([loadLibrary(), loadMuscles(), api.muscleCoverage().catch(() => [])]).then(([lib, , coverage]) => {
    library = lib;
    const byId = new Map((coverage || []).map((c) => [c.muscle_id, c]));
    for (const m of muscles()) {
      const c = byId.get(m.muscle_id);
      const name = String(m.muscle).toLowerCase();
      if (!c) gaps.never.push(name);
      else if (c.is_secondary && !c.is_primary) gaps.indirect.push(name);
    }
    renderStep();
  });

  function focus() { return FOCUS_BY_ID[data.focusId]; }
  function category() { return focus()?.category || "strength"; }
  function isLifting() { return category() === "strength"; }

  // Step list adapts to the chosen focus: custom adds a muscle picker, lifting
  // focuses add the goal step; cardio/mobility skip the goal.
  function steps() {
    const f = focus();
    const s = ["focus"];
    if (!f) return s;
    if (f.custom) s.push("muscles");
    if (f.category === "strength" || !f.category) s.push("goal");
    s.push("equipment", "generate");
    return s;
  }

  function renderStep() {
    const list = steps();
    const name = list[step];
    let content;
    if (name === "focus") content = focusStep();
    else if (name === "muscles") content = muscleStep();
    else if (name === "goal") content = goalStep();
    else if (name === "equipment") content = equipmentStep();
    else content = generateStep();
    setChildren(body,
      h("div", { class: "dim", style: { marginBottom: "10px" } }, `Step ${step + 1} of ${list.length}`),
      content
    );
  }

  function nav({ nextLabel = "Next", canNext = true, onNext } = {}) {
    return h("div", { class: "btn-row mt-lg" },
      step > 0 && h("button", { class: "btn btn-ghost grow", onClick: () => { step--; renderStep(); } }, "Back"),
      h("button", { class: "btn btn-primary grow", disabled: !canNext, onClick: onNext || (() => { step++; renderStep(); }) }, nextLabel)
    );
  }

  // ── Step: focus / split ─────────────────────────────────────────────────────
  function focusStep() {
    return h("div", null,
      h("h3", null, "Pick a focus"),
      h("p", { class: "dim" }, "Trainer-built splits that pair muscles the way they're usually programmed."),
      h("div", { class: "stat-grid mt" },
        ...FOCUSES.map((f) =>
          h("button", {
            class: ["stat", "card-tap"],
            style: { textAlign: "left", border: data.focusId === f.id ? "2px solid var(--accent)" : null },
            onClick: () => { data.focusId = f.id; data.count = f.count; if (!f.custom) data.muscles = new Set(); step = 1; generated = []; renderStep(); },
          },
            h("div", { class: "hstack", style: { gap: "8px", alignItems: "center" } },
              h("div", { style: { fontSize: "1.5rem" } }, f.emoji),
              h("div", { style: { fontWeight: 700 } }, f.label)
            ),
            h("div", { class: "dim", style: { fontSize: "0.74rem", marginTop: "4px" } }, f.sub)
          )
        )
      )
    );
  }

  // ── Step: custom muscle picker ──────────────────────────────────────────────
  // Selection lives in data.muscles (lowercased canonical names). The body map, the
  // removable "selected" chips and the gap-finder chips all read/write that one Set;
  // a shared refresh() repaints them in place so toggling never rebuilds the SVG.
  function muscleStep() {
    const map = MuscleMap({ selectable: true, selected: data.muscles, onToggle: () => refresh() });
    const selectedRow = h("div", { class: "chip-row mt" });
    const gapWrap = h("div", { class: "mt" });
    const navWrap = h("div");

    const toggle = (n) => { data.muscles.has(n) ? data.muscles.delete(n) : data.muscles.add(n); map._repaint?.(); refresh(); };

    // One gap bucket: a labelled group of tappable chips + an "Add all" shortcut.
    function gapGroup(label, names, hint) {
      const pending = names.filter((n) => !data.muscles.has(n));
      return h("div", { class: "mt" },
        h("div", { class: "row-between", style: { alignItems: "center", gap: "8px" } },
          h("span", { class: "dim", style: { fontSize: ".78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".02em" } }, `${label} (${names.length})`),
          pending.length > 1
            ? h("button", { class: "btn btn-ghost", style: { padding: "2px 10px", fontSize: ".76rem" }, onClick: () => { pending.forEach((n) => data.muscles.add(n)); map._repaint?.(); refresh(); } }, "Add all")
            : null
        ),
        hint ? h("div", { class: "dim", style: { fontSize: ".72rem", marginTop: "2px" } }, hint) : null,
        h("div", { class: "chip-row", style: { marginTop: "6px" } },
          ...names.map((n) => {
            const on = data.muscles.has(n);
            return h("button", { class: ["chip", on && "on"], style: { textTransform: "capitalize" }, onClick: () => toggle(n) },
              n, icon(on ? "check" : "plus", { size: 12 }));
          })
        )
      );
    }

    function refresh() {
      // Selected muscles as removable chips — trims work even where two muscles share one
      // body-map region (e.g. abductors/glutes), which a map tap alone can't separate.
      setChildren(selectedRow, ...[...data.muscles].map((m) =>
        h("span", { class: "chip on", style: { cursor: "pointer", textTransform: "capitalize" }, title: "Remove", onClick: () => toggle(m) }, m, icon("x", { size: 12 }))));
      const hasGaps = gaps.never.length || gaps.indirect.length;
      setChildren(gapWrap,
        hasGaps
          ? h("div", { class: "card", style: { background: "var(--surface-2)" } },
              h("div", { style: { fontWeight: 700, fontSize: ".92rem" } }, "Fill the gaps"),
              h("div", { class: "dim", style: { fontSize: ".74rem", marginTop: "2px" } }, "Muscles your past workouts have missed — you decide what counts as untrained."),
              gaps.never.length ? gapGroup("Never trained", gaps.never, "Not worked in any exercise yet.") : null,
              gaps.indirect.length ? gapGroup("Only worked indirectly", gaps.indirect, "Hit as a secondary mover, but never the main target.") : null)
          : h("div", { class: "dim", style: { fontSize: ".82rem" } }, "You've trained every muscle group — nice work."));
      // Re-render the nav too so "Next" enables/disables as the selection changes
      // (refresh() runs on in-place chip/map toggles that don't rebuild the whole step).
      setChildren(navWrap, nav({ canNext: data.muscles.size > 0 }));
    }
    refresh();

    return h("div", null,
      h("h3", null, "Target muscles"),
      h("p", { class: "dim" }, "Tap muscles on the body, or use the gap-finder below."),
      h("div", { class: "card mt", style: { padding: "10px" } }, map),
      selectedRow,
      gapWrap,
      navWrap
    );
  }

  // ── Step: goal / rep scheme ─────────────────────────────────────────────────
  function goalStep() {
    return h("div", null,
      h("h3", null, "What's the goal?"),
      h("p", { class: "dim" }, "Sets the rep ranges, sets and rest the wizard programs."),
      h("div", { class: "mt" },
        ...GOALS.map((g) =>
          h("button", {
            class: ["card", "card-tap"],
            style: { display: "flex", alignItems: "center", gap: "12px", width: "100%", textAlign: "left", marginBottom: "10px", border: data.goal === g.id ? "2px solid var(--accent)" : null },
            onClick: () => { data.goal = g.id; renderStep(); },
          },
            h("div", { style: { fontSize: "1.6rem" } }, g.emoji),
            h("div", null,
              h("div", { style: { fontWeight: 700 } }, g.label),
              h("div", { class: "dim", style: { fontSize: "0.8rem" } }, g.sub)
            )
          )
        )
      ),
      nav()
    );
  }

  // ── Step: equipment ─────────────────────────────────────────────────────────
  function equipmentStep() {
    const equipments = [...new Set(library.map((e) => e.equipment).filter(Boolean))].sort();
    return h("div", null,
      h("h3", null, "Available equipment"),
      h("p", { class: "dim" }, "Pick what you have, or skip for any."),
      h("div", { class: "chip-row mt" },
        ...equipments.map((eq) => {
          const chip = h("button", { class: ["chip", data.equipment.has(eq) && "on"], onClick: () => { data.equipment.has(eq) ? data.equipment.delete(eq) : data.equipment.add(eq); chip.classList.toggle("on"); } }, eq);
          return chip;
        })
      ),
      nav({ nextLabel: "Build workout", onNext: () => { step++; generate(); renderStep(); } })
    );
  }

  // ── Generation ──────────────────────────────────────────────────────────────
  function targetIds() {
    const f = focus();
    if (f.custom) {
      const names = [...data.muscles].map((n) => n.toLowerCase());
      return muscles().filter((m) => names.includes(m.muscle.toLowerCase())).map((m) => m.muscle_id);
    }
    return f.muscles || [];
  }

  function candidatePool(targets) {
    const targetSet = new Set(targets);
    return library.filter((ex) => {
      if ((ex.category || "").toLowerCase() !== category()) return false;
      if (data.equipment.size && !data.equipment.has(ex.equipment)) return false;
      if (targetSet.size) {
        const hits = (ex.primary_muscle_ids || []).some((id) => targetSet.has(id));
        if (!hits) return false;
      }
      return true;
    });
  }

  // Build one balanced workout: guarantee coverage of each target (biggest first),
  // cap per-muscle volume, prefer compounds, then order compounds → isolation.
  function buildOne(pool, targets, count) {
    const targetSet = new Set(targets);
    const owner = (ex) => {
      const matched = (ex.primary_muscle_ids || []).filter((id) => targetSet.has(id));
      return matched.length ? matched.reduce((b, id) => (prio(id) < prio(b) ? id : b)) : null;
    };

    let chosen;
    if (!targets.length) {
      // Cardio / mobility — no muscle targeting, just take a varied handful.
      chosen = shuffle(pool).slice(0, count);
    } else {
      const orderedTargets = [...targets].sort((a, b) => prio(a) - prio(b));
      const buckets = new Map(orderedTargets.map((t) => [t, []]));
      for (const ex of shuffle(pool)) {
        const o = owner(ex);
        if (o != null) buckets.get(o).push(ex);
      }
      for (const list of buckets.values()) list.sort((a, b) => (isCompound(a) ? 0 : 1) - (isCompound(b) ? 0 : 1));

      const used = new Set();
      const per = new Map(orderedTargets.map((t) => [t, 0]));
      const cap = Math.max(2, Math.ceil(count / targets.length) + 1);
      chosen = [];
      const take = (ex, o) => { used.add(ex.id); per.set(o, per.get(o) + 1); chosen.push(ex); };
      // Buckets are compound-first; preferIso flips that for accessory slots.
      const firstFree = (o, preferIso) => {
        const list = buckets.get(o);
        if (preferIso) { const iso = list.find((e) => !used.has(e.id) && !isCompound(e)); if (iso) return iso; }
        return list.find((e) => !used.has(e.id));
      };

      // Pass 1 — one compound anchor per target, biggest muscles first.
      for (const t of orderedTargets) {
        if (chosen.length >= count) break;
        const ex = firstFree(t, false);
        if (ex) take(ex, t);
      }
      // Pass 2 — fill remaining slots with accessories (isolation preferred), favouring
      // under-served muscles and respecting the per-muscle cap.
      while (chosen.length < count) {
        let progressed = false;
        for (const t of [...orderedTargets].sort((a, b) => per.get(a) - per.get(b))) {
          if (chosen.length >= count) break;
          if (per.get(t) >= cap) continue;
          const ex = firstFree(t, true);
          if (ex) { take(ex, t); progressed = true; }
        }
        if (!progressed) break;
      }
    }

    // Compounds first, then by muscle priority — big lifts open the session.
    chosen.sort((a, b) => {
      const c = (isCompound(a) ? 0 : 1) - (isCompound(b) ? 0 : 1);
      if (c) return c;
      const oa = owner(a), ob = owner(b);
      return prio(oa == null ? 99 : oa) - prio(ob == null ? 99 : ob);
    });

    return chosen.map((ex) => {
      const s = isLifting() ? scheme(data.goal, ex.mechanic) : nonLiftingScheme(category());
      return {
        library_exercise_id: ex.id,
        exercise_name: ex.exercise_name,
        images: ex.images,
        mechanic: ex.mechanic,
        force: ex.force,
        primary_muscle_ids: ex.primary_muscle_ids,
        secondary_muscle_ids: ex.secondary_muscle_ids,
        target_sets: s.sets,
        target_reps_min: s.min,
        target_reps_max: s.max,
        rest_seconds: s.rest,
      };
    });
  }

  function generate() {
    const targets = targetIds();
    const pool = candidatePool(targets);
    const f = focus();
    const goalLabel = isLifting() ? ` · ${GOALS.find((g) => g.id === data.goal).label}` : "";
    generated = [0, 1, 2].map((i) => ({
      name: `${f.label}${goalLabel} ${String.fromCharCode(65 + i)}`,
      targets,
      exercises: buildOne(pool, targets, data.count),
    }));
  }

  // ── Step: generate / preview ────────────────────────────────────────────────
  function generateStep() {
    if (!generated.length) generate();
    const empty = !generated[0] || !generated[0].exercises.length;
    return h("div", null,
      h("h3", null, empty ? "No matches" : "Pick a workout"),
      empty
        ? h("p", { class: "dim" }, "Not enough exercises matched your filters. Go back and loosen the equipment or focus.")
        : countAndRegen(),
      empty ? nav({ canNext: false }) : h("div", { class: "mt" }, ...generated.map(optionCard)),
    );
  }

  function countAndRegen() {
    const setCount = (n) => { data.count = Math.max(3, Math.min(10, n)); generate(); renderStep(); };
    return h("div", { class: "row-between mt", style: { alignItems: "center", gap: "10px", flexWrap: "wrap" } },
      h("div", { class: "hstack", style: { gap: "8px", alignItems: "center" } },
        h("span", { class: "dim", style: { fontSize: ".85rem" } }, "Exercises"),
        h("button", { class: "btn btn-ghost", style: { padding: "4px 12px" }, onClick: () => setCount(data.count - 1) }, "−"),
        h("strong", { style: { minWidth: "18px", textAlign: "center" } }, String(data.count)),
        h("button", { class: "btn btn-ghost", style: { padding: "4px 12px" }, onClick: () => setCount(data.count + 1) }, "+")
      ),
      h("button", { class: "btn btn-ghost", onClick: () => { generate(); renderStep(); } }, "🔀 Regenerate")
    );
  }

  function summary(opt) {
    const exs = opt.exercises;
    const totalSets = exs.reduce((s, e) => s + (e.target_sets || 0), 0);
    const mins = Math.round(exs.reduce((s, e) => s + e.target_sets * ((e.rest_seconds || 60) + 40), 0) / 60);
    const compounds = exs.filter(isCompound).length;
    const chips = [
      `${exs.length} exercises`,
      `~${mins} min`,
      `${totalSets} sets`,
    ];
    if (opt.targets.length) {
      const covered = new Set();
      for (const e of exs) for (const id of e.primary_muscle_ids || []) if (opt.targets.includes(id)) covered.add(id);
      chips.push(`${covered.size}/${opt.targets.length} muscles`);
    }
    if (isLifting()) chips.push(`${compounds} compound · ${exs.length - compounds} isolation`);
    return chips;
  }

  function optionCard(opt) {
    const primary = opt.exercises.flatMap((e) => e.primary_muscle_ids || []);
    const mini = MuscleMap({ primary });
    mini.classList.add("mini");
    mini.style.maxWidth = "108px";

    return h("div", { class: "card", style: { marginBottom: "14px" } },
      h("div", { class: "hstack", style: { alignItems: "flex-start", gap: "10px" } },
        mini,
        h("div", { class: "grow", style: { minWidth: 0 } },
          h("div", { style: { fontWeight: 700, fontSize: "1.05rem" } }, opt.name),
          h("div", { class: "chip-row", style: { marginTop: "6px", gap: "6px" } },
            ...summary(opt).map((c) => h("span", { class: "chip", style: { fontSize: ".72rem" } }, c))
          )
        )
      ),
      h("div", { class: "mt", style: { borderTop: "1px solid var(--border)", paddingTop: "6px" } },
        ...opt.exercises.map(exRow)
      ),
      h("button", { class: "btn btn-primary btn-block mt", onClick: () => { modal.close(); onComplete?.(stripPreviewFields(opt)); } }, "Use this workout")
    );
  }

  function exRow(e) {
    const reps = e.target_reps_min ? `${e.target_reps_min}${e.target_reps_max && e.target_reps_max !== e.target_reps_min ? `–${e.target_reps_max}` : ""}` : null;
    const scheme = reps ? `${e.target_sets}×${reps}` : `${e.target_sets} sets`;
    const m = (e.primary_muscle_ids || [])[0];
    return h("div", { class: "row-between", style: { padding: "5px 0", alignItems: "center", gap: "10px" } },
      h("div", { style: { minWidth: 0 } },
        h("div", { style: { fontWeight: 600, fontSize: ".92rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }, e.exercise_name),
        h("div", { class: "dim", style: { fontSize: ".74rem" } },
          isCompound(e) ? "Compound" : "Isolation", m ? ` · ${muscleName(m)}` : "")
      ),
      h("span", { class: "dim", style: { fontSize: ".82rem", whiteSpace: "nowrap" } }, scheme, h("span", { style: { opacity: .6 } }, ` · ${e.rest_seconds}s`))
    );
  }

  return modal;
}

// Drop preview-only fields before handing the workout to the editor.
function stripPreviewFields(opt) {
  return {
    name: opt.name,
    exercises: opt.exercises.map(({ mechanic, force, ...keep }) => keep),
  };
}
